import { useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import { getAccessLogs } from "../services/api";

const AdminAccessLogs = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [area, setArea] = useState("");
  const [userName, setUserName] = useState("");
  const [q, setQ] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const totalPages = useMemo(() => {
    return Math.max(Math.ceil(total / (limit || 1)), 1);
  }, [total, limit]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = { page, limit };
      if (area) params.area = area;
      if (userName) params.userName = userName;
      if (q) params.q = q;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const res = await getAccessLogs(params);
      const payload = res?.data || {};
      setData(payload.data || []);
      setTotal(payload.total || 0);
    } catch (error) {
      enqueueSnackbar("Erro ao carregar logs", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  const handleApplyFilters = () => {
    setPage(1);
    fetchLogs();
  };

  const handleClearFilters = () => {
    setArea("");
    setUserName("");
    setQ("");
    setStartDate("");
    setEndDate("");
    setPage(1);
    fetchLogs();
  };

  const formatDateTime = (iso) => {
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleString("pt-BR");
    } catch (_) {
      return "";
    }
  };

  return (
    <div className="p-4 mt-24">
      <h1 className="text-2xl font-bold mb-4">Logs de Acesso</h1>

      {/* Filtros */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-4">
        <div>
          <label className="label">
            <span className="label-text">Área</span>
          </label>
          <select
            className="select select-bordered w-full"
            value={area}
            onChange={(e) => setArea(e.target.value)}
          >
            <option value="">Todas</option>
            <option value="public">Public</option>
            <option value="shop">Shop</option>
          </select>
        </div>

        <div>
          <label className="label">
            <span className="label-text">Login (nome)</span>
          </label>
          <input
            className="input input-bordered w-full"
            type="text"
            placeholder="Ex.: João"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
          />
        </div>

        <div>
          <label className="label">
            <span className="label-text">Busca (página/caminho)</span>
          </label>
          <input
            className="input input-bordered w-full"
            type="text"
            placeholder="Ex.: Shop, /ar/shop"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        <div>
          <label className="label">
            <span className="label-text">Início</span>
          </label>
          <input
            className="input input-bordered w-full"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div>
          <label className="label">
            <span className="label-text">Fim</span>
          </label>
          <input
            className="input input-bordered w-full"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <button className="btn btn-primary" onClick={handleApplyFilters} disabled={loading}>
          Aplicar filtros
        </button>
        <button className="btn" onClick={handleClearFilters} disabled={loading}>
          Limpar
        </button>
        <div className="ml-auto flex items-center gap-2">
          <span>Por página:</span>
          <select
            className="select select-bordered"
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value, 10) || 20)}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      {/* Tabela */}
      <div className="overflow-x-auto">
        <table className="table table-zebra w-full">
          <thead>
            <tr>
              <th>Data/Hora</th>
              <th>Área</th>
              <th>Página</th>
              <th>Caminho</th>
              <th>Login</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr key={row._id}>
                <td>{formatDateTime(row.createdAt)}</td>
                <td>{row.area}</td>
                <td>{row.page || ""}</td>
                <td>{row.path || ""}</td>
                <td>{row.userName || ""}</td>
              </tr>
            ))}
            {data.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-4">
                  {loading ? "Carregando..." : "Nenhum registro"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Paginação */}
      <div className="flex items-center justify-between mt-4">
        <div>
          Total: {total} | Página {page} de {totalPages}
        </div>
        <div className="flex gap-2">
          <button
            className="btn"
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            disabled={page <= 1 || loading}
          >
            « Anterior
          </button>
          <button
            className="btn"
            onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
            disabled={page >= totalPages || loading}
          >
            Próxima »
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminAccessLogs;